import React, { useState } from 'react';
import { X, Sparkles, Check, Crown } from 'lucide-react';
import { useAuth } from '../AuthContext';

const UpgradeModal = ({ isOpen, onClose, feature }) => {
  const { token, user } = useAuth();  // Get user to check trial status
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if user is on trial
  const isOnTrial = user?.subscription_status === 'growth_trial' || user?.subscription_plan === 'growth_trial';
  const hasActiveSubscription = user?.subscription_status === 'active';

  const handleUpgrade = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      if (!token) {
        setError('Please log in to upgrade');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ billing_period: billingPeriod }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to start checkout. Please try again.');
      }
    } catch (error) {
      console.error('Error starting checkout:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const comparisons = [
    { feature: 'Daily affirmations', free: '1 per day', growth: 'Unlimited' },
    { feature: 'Conchi conversations', free: '5 per day', growth: 'Unlimited', highlight: true },
    { feature: 'AI journal insights', free: '1 per week', growth: 'Unlimited' },
    { feature: 'Love Garden sessions', free: 'Limited', growth: 'Unlimited' },
    { feature: 'Divine Guidance', free: '1 per week', growth: 'Unlimited' },
    { feature: 'Vision Board Maker', free: '1 board', growth: 'Unlimited' },
    { feature: 'Love Vault saves', free: '10 per month', growth: 'Unlimited' },
  ];

  const features = [
    { icon: '💬', text: 'Unlimited conversations with Conchi', highlight: true },
    { icon: '✨', text: 'Unlimited AI insights from your journal' },
    { icon: '🌟', text: 'Unlimited personalized affirmations' },
    { icon: '🌸', text: 'Unlimited Love Garden sessions' },
    { icon: '🔮', text: 'Unlimited Divine Guidance readings' },
    { icon: '💕', text: 'Unlimited Love Vault saves' },
    { icon: '🎨', text: 'Unlimited Vision Boards' },
    ...(isOnTrial ? [] : [{ icon: '👑', text: '7-day free trial included' }]),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-pink-50 via-white to-purple-50 rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="p-8 pb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">
            {hasActiveSubscription ? 'Your Growth Plan' : 'Upgrade to Growth Plan'}
          </h2>
          <p className="text-gray-600 text-lg">
            {hasActiveSubscription 
              ? 'Manage your subscription'
              : isOnTrial 
              ? 'Complete your subscription setup'
              : 'Start your 7-day free trial • Cancel anytime'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-8 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Pricing */}
        <div className="px-8 pb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-pink-200">
            <div className="text-center mb-4">
              <p className="text-4xl font-bold text-gray-900">$17<span className="text-lg font-normal text-gray-600">/month</span></p>
              <p className="text-sm text-gray-500 mt-2">
                {isOnTrial 
                  ? '$17/month • Cancel anytime' 
                  : '7-day free trial • Then $17/month • Cancel anytime'}
              </p>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {features.map((item, index) => (
              <div key={index} className={`flex items-center gap-3 p-3 rounded-lg transition-all ${item.highlight ? 'bg-gradient-to-r from-pink-100 to-purple-100' : 'bg-white'}`}>
                <span className="text-2xl">{item.icon}</span>
                <span className={`text-sm ${item.highlight ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={handleUpgrade}
            disabled={isLoading || hasActiveSubscription}
            className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span>Processing...</span>
            ) : hasActiveSubscription ? (
              <span>Already Subscribed ✓</span>
            ) : isOnTrial ? (
              <span>Continue to Payment →</span>
            ) : (
              <span>Start 7-Day Free Trial →</span>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;